import React, { useState, useEffect, useMemo } from "react";
import {
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Box,
  FormControlLabel,
  Switch,
  Pagination,
  TextField,
  Stack,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Backdrop,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
  Alert
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import GetAppIcon from "@mui/icons-material/GetApp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import PersonIcon from "@mui/icons-material/Person";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import PaymentIcon from "@mui/icons-material/Payment";
import StarIcon from "@mui/icons-material/Star";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CurrencyBitcoinIcon from "@mui/icons-material/CurrencyBitcoin";
import LockIcon from "@mui/icons-material/Lock";
import FeePayerIcon from "../../../../Components/payments/FeePayerIcon";
import { useMutation, useQuery } from "react-query";
import client from "../../../../API";
import { StyledTableRow, StyledTableCell } from "../../../../Utils/styledcell";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Paper from "@mui/material/Paper";
import PayoutDialog from "../payoutdialog";
import CreatorPaymentDialog from "../helpers/creatortaxwalletinfo";
import { drawerWidth, payoutStatuses } from "../../../../Utils/constants";
import { useIsMounted } from "../../../../Hooks/use-is-mounted";
import {
  StyleButton,
  StyleContent,
  StyleStickyButtonContainter, 
  StyleContentWithNavBar,
  StyleButtonContainter
} from "../../../../Utils/ThemedStyles";
import { toast } from 'sonner';

// Helper function to get wallet icon
const getWalletIcon = (walletType) => {
  switch (walletType) {
    case 'paypal':
      return <PaymentIcon fontSize="small" />;
    case 'stripe':
      return <PaymentIcon fontSize="small" />;
    case 'wise':
      return <AccountBalanceWalletIcon fontSize="small" />;
    case 'bank_ach':
      return <AccountBalanceIcon fontSize="small" />;
    case 'crypto':
      return <CurrencyBitcoinIcon fontSize="small" />;
    default:
      return <AccountBalanceWalletIcon fontSize="small" />;
  }
};

// Helper function to format wallet display name
const formatWalletDisplayName = (wallet) => {
  if (wallet.nickname) return wallet.nickname;
  
  const type = wallet.wallet_type.replace('_', ' ');
  switch (wallet.wallet_type) {
    case 'paypal':
      return `PayPal (${wallet.paypal_email?.slice(-10) || '***'})`;
    case 'stripe':
      return `Stripe (${wallet.stripe_status || 'Connected'})`;
    case 'wise':
      return `Wise ${wallet.wise_currency || 'USD'}`;
    case 'bank_ach':
      return `${wallet.bank_name || 'Bank'} (${wallet.bank_account_number || ''})`;
    case 'crypto':
      return `${wallet.crypto_currency || 'Crypto'} Wallet`;
    default:
      return type.charAt(0).toUpperCase() + type.slice(1);
  }
};

// Component for displaying primary wallet info
const PrimaryWalletDisplay = ({ payout, onPaymentInfoClick }) => {
  const primaryWallet = payout.primary_wallet;

  // Handle case where no wallet exists (legacy payouts)
  if (!primaryWallet) {
    return (
      <Tooltip title="No modern wallets configured">
        <Chip
          label="Legacy Payment"
          size="small"
          color="warning"
          variant="outlined"
          onClick={onPaymentInfoClick}
          sx={{ cursor: 'pointer' }}
        />
      </Tooltip>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={`Primary wallet: ${formatWalletDisplayName(primaryWallet)}`}>
        <Chip
          icon={getWalletIcon(primaryWallet.wallet_type)}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <StarIcon sx={{ fontSize: 12 }} />
              {formatWalletDisplayName(primaryWallet)}
            </Box>
          }
          size="small"
          color={primaryWallet.verification_status === 'verified' ? 'success' : 'default'}
          variant="outlined"
          onClick={onPaymentInfoClick}
          sx={{ cursor: 'pointer', maxWidth: 200 }}
        />
      </Tooltip>

      {primaryWallet.has_multiple && (
        <IconButton
          size="small"
          onClick={onPaymentInfoClick}
          title="View all wallets"
        >
          <MoreVertIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );
};

const PayoutDetailsView = () => {
  const isMounted = useIsMounted();
  const [selectedIds, setSelectedIds] = useState([]);
  const [isPayoutDialogOpen, setPayoutDialogOpen] = useState(false);
  const [editingPayout, setEditingPayout] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [downloadingCsv, setDownloadingCsv] = useState(false);

  // Creator payment dialog state
  const [isCreatorPaymentDialogOpen, setCreatorPaymentDialogOpen] = useState(false);
  const [selectedCreator, setSelectedCreator] = useState(null);

  const [page, setPage] = useState(0);
  const itemsPerPage = 10;
  const [usePagination, setUsePagination] = useState(true);

  const { data: payouts, refetch: fetchPayouts, error, isFetching } = useQuery({
    queryKey: ['payouts'],
    queryFn: () => client.payouts.listAdmin(),
    refetchOnWindowFocus: true,   // Enable refetch when user returns to tab (fixes automated payout visibility)
    retry: 2,  // Retry failed requests up to 2 times
    staleTime: 30 * 1000,         // 30 seconds - balance between freshness and performance
    cacheTime: 5 * 60 * 1000      // 5 minutes - shorter cache duration for fresher data
  });

  // Mutation for updating payout method
  const updatePayoutMutation = useMutation({
    mutationFn: async ({ payoutId, walletInfo }) => {
      return await client.payouts.editAdmin({
        payoutId: payoutId,
        payout_method: walletInfo.wallet_type,
        // You might want to store additional wallet info in notes or a separate field
        notes: `Updated payout method to: ${formatWalletDisplayName(walletInfo)}`
      });
    },
    onSuccess: () => {
      fetchPayouts();
      toast.success('Payout method updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update payout method');
      console.error('Error updating payout method:', error);
    }
  });

  useEffect(() => {
    setSelectedIds([]);
  }, [payouts]);

  useEffect(() => {
    if (error) {
      alert(error?.response?.data?.error || error?.response?.data?.message || error.message);
      console.error("Error fetching payouts:", error);
    }
  }, [error]);

  // CSV Export functionality
  const onExportToCsv = async () => {
    if (selectedIds.length === 0) return;
    
    setDownloadingCsv(true);
    try {
      const csvData = [];
      const headers = [
        'Payout ID',
        'PO Number',
        'Creator Name',
        'Creator Status',
        'Payout Date',
        'Approval Date',
        'Accepted Date',
        'Status',
        'BlitzPay',
        'Interest Rate',
        'Subtotal',
        'Fee',
        'Total',
        'Who\'s Paying Fee',
        'Client Name',
        'Company Name',
        'Payout Method',
        'Days to Payment',
        'Source Type',
        'Unique Code',
        'Is Creator Connected',
        'Notes'
      ];

      csvData.push(headers);

      const payoutsToExport = filteredPayouts.filter(payout => 
        selectedIds.includes(payout.id)
      );

      payoutsToExport.forEach(payout => {
        const totalAmount = payout.net_amount ?? payout.amount;
        const feeDisplay = payout.role === 'Client'
          ? 'Client covers'
          : `$${(payout.fee_amount || 0).toFixed(2)}${payout.rail_fee_amount > 0 ? ` + $${payout.rail_fee_amount.toFixed(2)} rail` : ''}`;

        const row = [
          payout.id,
          payout.po_number || '',
          `${payout.creatorName || ''} (${payout.creatorStatus || ''})`,
          payout.creatorStatus || '',
          payout.payout_date ? new Date(payout.payout_date).toLocaleDateString() : '',
          payout.approval_date ? new Date(payout.approval_date).toLocaleDateString() : '',
          payout.accepted_date ? new Date(payout.accepted_date).toLocaleDateString() : '',
          payout.status || '',
          payout.blitzpay ? "Yes" : "No",
          `${payout.interest_rate || 0}%`,
          `$${payout.amount?.toFixed(2) || '0.00'}`,
          feeDisplay,
          `$${totalAmount?.toFixed(2) || '0.00'}`,
          payout.role || '',
          payout.clientName || '',
          payout.companyName || '',
          payout.payout_method || '',
          payout.days_to_payment || 0,
          payout.source_type || '',
          payout.unique_code || '',
          payout.isCreatorConnected || 'Unknown',
          payout.notes || ''
        ];
        csvData.push(row);
      });

      // Convert to CSV string
      const csvContent = csvData.map(row => 
        row.map(field => 
          typeof field === 'string' && field.includes(',') 
            ? `"${field}"` 
            : field
        ).join(',')
      ).join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `payouts_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error('Error exporting to CSV:', error);
      alert('Error exporting to CSV. Please try again.');
    } finally {
      setDownloadingCsv(false);
    }
  };

  const onDeletePayouts = async () => {
    if (selectedIds.length > 0) {
      try {
        const data = await client.payouts.delete({ payoutIds: selectedIds });
        toast.success('Payout details deleted successfully', {
          description: 'Record has been removed'
        });
        fetchPayouts();
      } catch (error) {
        console.error("Error deleting payouts", error);
      }
    }
  };

  const handleDialogOpen = (payout) => {
    setEditingPayout(payout);
    setPayoutDialogOpen(true);
  };

  const handleDialogClose = (changed) => {
    setPayoutDialogOpen(false);
    setEditingPayout(null);
    if(changed)
      fetchPayouts();
  };

  // Creator payment dialog handlers
  const handleCreatorPaymentDialogOpen = (creatorId, creatorName) => {
    setSelectedCreator({ id: creatorId, name: creatorName });
    setCreatorPaymentDialogOpen(true);
  };

  const handleCreatorPaymentDialogClose = () => {
    setCreatorPaymentDialogOpen(false);
    setSelectedCreator(null);
  };

  // Handle wallet selection for payout
  const handleWalletSelect = (payout, wallet) => {
    updatePayoutMutation.mutate({
      payoutId: payout.id,
      walletInfo: wallet
    });
  };

  const handleSelectPayout = (payoutId) => {
    const newSelectedIds = selectedIds.includes(payoutId)
      ? selectedIds.filter(id => id !== payoutId)
      : [...selectedIds, payoutId];
    setSelectedIds(newSelectedIds);
  };

  const handleFilterChange = (event) => {
    setStatusFilter(event.target.value === "all" ? "" : event.target.value);
    setPage(0);
  };

  // Filter payouts
  const filteredPayouts = useMemo(() => {
    if (!payouts) return [];
    return statusFilter
      ? payouts.filter(payout => payout.status === statusFilter)
      : payouts;
  }, [payouts, statusFilter]);

  // Sorting
  const sortedPayouts = useMemo(() => {
    const sorted = [...filteredPayouts];

    switch (sortOption) {
      case "amount_desc":
        return sorted.sort((a, b) => (b.amount || 0) - (a.amount || 0));
      case "amount_asc":
        return sorted.sort((a, b) => (a.amount || 0) - (b.amount || 0));
      case "payout_date_desc":
        return sorted.sort((a, b) => {
          const dateA = new Date(a.payout_date || 0).getTime();
          const dateB = new Date(b.payout_date || 0).getTime();
          return dateB - dateA;
        });
      case "payout_date_asc":
        return sorted.sort((a, b) => {
          const dateA = new Date(a.payout_date || 0).getTime();
          const dateB = new Date(b.payout_date || 0).getTime();
          return dateA - dateB;
        });
      case "status":
        return sorted.sort((a, b) => (a.status || '').localeCompare(b.status || ''));
      default:
        return sorted;
    }
  }, [filteredPayouts, sortOption]);

  const pagedPayouts = usePagination ? sortedPayouts.slice(page * itemsPerPage, Math.min((page * itemsPerPage) + itemsPerPage, sortedPayouts.length)) : sortedPayouts;

  return (
    <Box sx={StyleContentWithNavBar}>
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer - 1 }}
        open={isFetching}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load payouts: {error.message}. Please try refreshing the page.
        </Alert>
      )}

      {/* Top controls */}
      <Box
        sx={{
          ...StyleButtonContainter,
          flexDirection: 'column',
          alignItems: 'stretch',
        }}
      >
        {/* Bulk Actions */}
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Button
            variant="contained"
            onClick={onDeletePayouts}
            startIcon={<DeleteIcon />}
            disabled={!selectedIds.length}
            sx={StyleButton}
          >
            Delete Selected ({selectedIds.length})
          </Button>

          <Button
            variant="contained"
            onClick={onExportToCsv}
            startIcon={downloadingCsv ? <CircularProgress size={16} /> : <GetAppIcon />}
            disabled={!selectedIds.length || downloadingCsv}
            sx={{ ...StyleButton, bgcolor: 'info.main' }}
          >
            {downloadingCsv ? 'Exporting...' : `Export CSV (${selectedIds.length})`}
          </Button>
        </Stack>

        {/* Filters */}
        <Accordion sx={{ width: '100%' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Filters & Sorting</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} flexWrap="wrap">
              {/* Status filter */}
              <TextField
                select
                value={statusFilter || "all"}
                onChange={handleFilterChange}
                label='Status'
                sx={{ minWidth: '10rem' }}
                SelectProps={{sx:{textTransform:'capitalize'}}}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                {payoutStatuses.map((payoutStatus) => <MenuItem key={payoutStatus} value={payoutStatus} sx={{ textTransform: 'capitalize' }}>{payoutStatus}</MenuItem>)}
              </TextField>

              {/* Sorting dropdown */}
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel id="sort-by-label">Sort by</InputLabel>
                <Select
                  labelId="sort-by-label"
                  value={sortOption}
                  label="Sort by"
                  onChange={(e) => {
                    setSortOption(e.target.value);
                    setPage(0);
                  }}
                >
                  <MenuItem value="">(No sorting)</MenuItem>
                  <MenuItem value="amount_desc">Amount: High to Low</MenuItem>
                  <MenuItem value="amount_asc">Amount: Low to High</MenuItem>
                  <MenuItem value="payout_date_desc">Payout Date: Most Recent</MenuItem>
                  <MenuItem value="payout_date_asc">Payout Date: Oldest</MenuItem>
                  <MenuItem value="status">Status: Alphabetical</MenuItem>
                </Select>
              </FormControl>

              {/* Pagination switch */}
              <FormControlLabel
                label='Use pagination'
                checked={usePagination}
                onChange={() => setUsePagination(prev => !prev)}
                color="primary"
                control={<Switch></Switch>}
              />
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>

      <TableContainer component={Paper} sx={(theme) => ({ marginTop: theme.spacing(2) })}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.length > 0 && selectedIds.length < filteredPayouts.length}
                  checked={selectedIds.length === filteredPayouts.length && filteredPayouts.length > 0}
                  onChange={(event) => setSelectedIds(
                    event.target.checked ? filteredPayouts.map((n) => n.id) : []
                  )}
                />
              </TableCell>
              <TableCell>Payout ID</TableCell>
              <TableCell>PO Number</TableCell>
              <TableCell>Creator Name</TableCell>
              <TableCell>Payout Date</TableCell>
              <TableCell>Approval Date</TableCell>
              <TableCell>Accepted Date</TableCell>
              <TableCell>Is Creator Connected</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>BlitzPay</TableCell>
              <TableCell>Subtotal</TableCell>
              <TableCell>Fee</TableCell>
              <TableCell>Total</TableCell>
              <TableCell>Who's Paying Fee?</TableCell>
              <TableCell>Client Name</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Payout Method</TableCell>
              <TableCell>Preferred Wallet</TableCell>
              <TableCell>Days to Payment</TableCell>
              <TableCell>Source Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pagedPayouts?.map((payout) => (
              <StyledTableRow key={payout.id}>
                <StyledTableCell padding="checkbox">
                  <Checkbox
                    onChange={() => handleSelectPayout(payout.id)}
                    checked={selectedIds.includes(payout.id)}
                  />
                </StyledTableCell>
                <StyledTableCell>{payout.id}</StyledTableCell>
                <StyledTableCell sx={{textWrap:'nowrap'}}>{payout.po_number}</StyledTableCell>
                
                {/* Creator Info with clickable payment info */}
                <StyledTableCell>
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip
                        icon={<PersonIcon />}
                        label={payout.creatorName || 'Unknown'}
                        size="small"
                        variant="outlined"
                        color="primary"
                        onClick={() => handleCreatorPaymentDialogOpen(payout.creator_id, payout.creatorName)}
                        sx={{ cursor: 'pointer' }}
                      />
                      <IconButton 
                        size="small" 
                        onClick={() => handleCreatorPaymentDialogOpen(payout.creator_id, payout.creatorName)}
                        title="View Payment Info"
                      >
                        <AccountBalanceWalletIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Typography variant="caption" color="textSecondary">
                      ({payout.creatorStatus || 'Unknown'})
                    </Typography>
                  </Stack>
                </StyledTableCell>
                
                <StyledTableCell>
                  {payout.payout_date ? new Date(payout.payout_date).toLocaleDateString() : 'Not set'}
                </StyledTableCell>
                <StyledTableCell>
                  {payout.approval_date ? new Date(payout.approval_date).toLocaleDateString() : 'Not approved'}
                </StyledTableCell>
                <StyledTableCell>
                  {payout.accepted_date ? new Date(payout.accepted_date).toLocaleDateString() : 'Not accepted'}
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={payout.isCreatorConnected || 'Unknown'}
                    size="small"
                    color={payout.isCreatorConnected === 'Yes' ? 'success' : 'default'}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={payout.status}
                    color={
                      payout.status === 'paid' ? 'success' :
                      payout.status === 'approved' || payout.status === 'confirmed' ? 'info' :
                      payout.status === 'pending' ? 'warning' :
                      payout.status === 'cancelled' ? 'error' : 'default'
                    }
                    size="small"
                    sx={{ textTransform: 'capitalize' }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={payout.blitzpay ? "Yes" : "No"}
                    color={payout.blitzpay ? "success" : "default"}
                    size="small"
                  />
                </StyledTableCell>
                <StyledTableCell>${payout.amount?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}</StyledTableCell>
                <StyledTableCell>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>
                      {(payout.display_fee_percentage ?? payout.interest_rate ?? 0)}% ($
                      {Number(payout.display_fee_amount ?? payout.fee_amount ?? ((payout.amount || 0) * ((payout.interest_rate || 0) / 100))).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      )
                    </span>
                    <FeePayerIcon payer={payout.fee_payer || (payout.role === 'Creator' ? 'creator' : 'me')} />
                  </Stack>
                  {(payout.rail_fee_amount || 0) > 0 && (
                    <Chip
                      size="small"
                      icon={<LockIcon />}
                      label={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <span>
                            {payout.forced_payment_rail ? `${payout.forced_payment_rail} rail` : 'Rail'}
                            : ${Number(payout.rail_fee_amount).toFixed(2)}
                          </span>
                          <FeePayerIcon payer={payout.rail_fee_payer} size={12} />
                        </Stack>
                      }
                      variant="outlined"
                      color="info"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  ${((payout.amount || 0)
                      - ((payout.fee_payer || (payout.role === 'Creator' ? 'creator' : 'me')) === 'creator' ? Number(payout.display_fee_amount ?? payout.fee_amount ?? ((payout.amount || 0) * ((payout.interest_rate || 0) / 100))) : 0)
                      - (payout.rail_fee_payer === 'creator' ? Number(payout.rail_fee_amount || 0) : 0)
                   ).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={payout.role || "Creator"}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                  {(payout.rail_fee_amount || 0) > 0
                    && payout.rail_fee_payer
                    && payout.rail_fee_payer !== (payout.fee_payer || (payout.role === 'Creator' ? 'creator' : 'me')) && (
                    <Stack direction="row" alignItems="center" spacing={0.25} sx={{ mt: 0.5 }}>
                      <Typography variant="caption" color="textSecondary">
                        {payout.forced_payment_rail ? `${payout.forced_payment_rail} rail fee` : 'Rail fee'}:
                      </Typography>
                      <FeePayerIcon payer={payout.rail_fee_payer} size={12} />
                    </Stack>
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  {payout.clientName && (
                    <Chip
                      icon={<BusinessIcon />}
                      label={payout.clientName}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  {payout.companyName && (
                    <Chip
                      icon={<BusinessIcon />}
                      label={payout.companyName}
                      size="small"
                      variant="outlined"
                      color="info"
                    />
                  )}
                </StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={payout.payout_method || 'Not specified'}
                    size="small"
                    variant="outlined"
                    color={payout.payout_method ? "success" : "default"}
                  />
                </StyledTableCell>
                {/* New Preferred Wallet Column */}
                <StyledTableCell sx={{ maxWidth: 250 }}>
                  {payout.creator_id ? (
                    <PrimaryWalletDisplay
                      payout={payout}
                      onPaymentInfoClick={() => handleCreatorPaymentDialogOpen(payout.creator_id, payout.creatorName)}
                    />
                  ) : (
                    <Typography variant="caption" color="textSecondary">
                      N/A
                    </Typography>
                  )}
                </StyledTableCell>
                <StyledTableCell>{payout.days_to_payment || 0} days</StyledTableCell>
                <StyledTableCell>
                  <Chip
                    label={payout.source_type || 'managed'}
                    size="small"
                    variant="outlined"
                    color="default"
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => handleDialogOpen(payout)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDialogOpen(payout)}>
                      {payout.status === 'approved' || payout.status === 'confirmed' ? 
                        <CheckCircleIcon color="success" /> : 
                        <CheckCircleOutlineIcon />
                      }
                    </IconButton>
                  </Stack>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      
      {usePagination && (
        <Box sx={{ paddingBlock: 2 }}>
          <Pagination
            sx={{ marginInline: 'auto', maxWidth: 'fit-content' }}
            count={Math.ceil(sortedPayouts.length / itemsPerPage)} 
            page={page + 1}
            onChange={(event, value) => setPage(value - 1)}
          />
        </Box>
      )}

      {/* Existing Payout Dialog */}
      {isPayoutDialogOpen && (
        <PayoutDialog
          open={isPayoutDialogOpen}
          onClose={handleDialogClose}
          payoutInfo={editingPayout}
        />
      )}

      {/* New Creator Payment Dialog */}
      {isCreatorPaymentDialogOpen && selectedCreator && (
        <CreatorPaymentDialog
          open={isCreatorPaymentDialogOpen}
          onClose={handleCreatorPaymentDialogClose}
          creatorId={selectedCreator.id}
          creatorName={selectedCreator.name}
        />
      )}
    </Box>
  );
};

export default PayoutDetailsView;
